import React, { useState } from 'react';
import { useJarvisContext } from '../../context/JarvisContext';
import { getRiskSettings, updateRiskSettings, getTrades } from '../../services/storage';
import {
  calcDollarRisk, calcLotSize, calcRR,
  calcTodayStats, getRiskStatus,
} from '../../utils/riskCalculator';

const THEME = {
  colors: {
    bg:        '#0a0e1a',
    card:      '#111827',
    border:    '#1e293b',
    text:      '#e2e8f0',
    textMuted: '#94a3b8',
    green:     '#00ff88',
    red:       '#ff3333',
    gold:      '#ffd700',
    blue:      '#00d4ff',
  },
};

const S = {
  page:    { padding: '16px', color: THEME.colors.text, fontFamily: 'monospace', maxWidth: '100%' },
  title:   { fontSize: '18px', fontWeight: '700', color: THEME.colors.gold, marginBottom: '4px', letterSpacing: '2px' },
  sub:     { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '16px', letterSpacing: '1px' },
  card:    (bg) => ({ background: bg || THEME.colors.card, border: `1px solid ${THEME.colors.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' }),
  section: { fontSize: '10px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '2px', marginBottom: '10px' },
  label:   { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' },
  input:   { width: '100%', background: '#0d1117', border: `1px solid ${THEME.colors.border}`, borderRadius: '6px', padding: '8px 10px', color: THEME.colors.text, fontSize: '13px', fontFamily: 'monospace', boxSizing: 'border-box' },
  row:     { display: 'flex', gap: '10px', marginBottom: '10px' },
  btn:     (c) => ({ background: c === 'green' ? 'rgba(0,255,136,0.15)' : c === 'red' ? 'rgba(255,51,51,0.15)' : c === 'primary' ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${c === 'green' ? THEME.colors.green : c === 'red' ? THEME.colors.red : c === 'primary' ? THEME.colors.blue : THEME.colors.border}`, borderRadius: '6px', padding: '8px 14px', color: c === 'green' ? THEME.colors.green : c === 'red' ? THEME.colors.red : c === 'primary' ? THEME.colors.blue : THEME.colors.text, fontSize: '12px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '600' }),
  stat:    (c) => ({ fontSize: '18px', fontWeight: '700', color: c || THEME.colors.gold }),
  badge:   (bg, col) => ({ background: bg, color: col, borderRadius: '4px', padding: '2px 7px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px' }),
};

function Inp({ label, ...props }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={S.label}>{label}</div>
      <input style={S.input} {...props} />
    </div>
  );
}

function Bar({ value, max, color }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', height: '8px', overflow: 'hidden', marginTop: '4px' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? THEME.colors.red : pct >= 70 ? THEME.colors.gold : color, borderRadius: '4px', transition: 'width 0.4s' }} />
    </div>
  );
}

// ─── Settings Panel ──────────────────────────────────────────────────────────
const safeNum = (v, fallback = '') => (v == null || (typeof v === 'number' && isNaN(v))) ? fallback : v;

function SettingsPanel({ settings, onSave }) {
  const [form, setForm] = useState({
    account_balance:        safeNum(settings.account_balance, 1000),
    risk_per_trade_percent: safeNum(settings.risk_per_trade_percent, 1),
    max_daily_loss_percent: safeNum(settings.max_daily_loss_percent, 3),
    max_daily_trades:       safeNum(settings.max_daily_trades, 3),
  });
  const set = (k, v) => {
    const num = parseFloat(v);
    setForm(f => ({ ...f, [k]: isNaN(num) ? v : num }));
  };
  const dollarRisk = calcDollarRisk(form.account_balance || 0, form.risk_per_trade_percent || 1);
  const maxLoss    = calcDollarRisk(form.account_balance || 0, form.max_daily_loss_percent || 3);

  return (
    <div style={S.card()}>
      <div style={S.section}>ACCOUNT & RISK SETTINGS</div>
      <div style={S.row}>
        <Inp label="Account Balance ($)" type="number" value={form.account_balance} onChange={e => set('account_balance', e.target.value)} />
        <Inp label="Risk / Trade (%)" type="number" step="0.1" min="0.1" max="5" value={form.risk_per_trade_percent} onChange={e => set('risk_per_trade_percent', e.target.value)} />
      </div>
      <div style={S.row}>
        <Inp label="Max Daily Loss (%)" type="number" step="0.5" min="1" max="10" value={form.max_daily_loss_percent} onChange={e => set('max_daily_loss_percent', e.target.value)} />
        <Inp label="Max Trades / Day" type="number" min="1" max="10" value={form.max_daily_trades} onChange={e => set('max_daily_trades', e.target.value)} />
      </div>
      <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '10px' }}>
        Risk per trade: <strong style={{ color: THEME.colors.gold }}>${dollarRisk}</strong> &nbsp;|&nbsp;
        Max daily loss: <strong style={{ color: THEME.colors.red }}>${maxLoss}</strong>
      </div>
      <button style={S.btn('primary')} onClick={() => { onSave(form); }}>
        Save Settings
      </button>
    </div>
  );
}

// ─── Position Sizer ──────────────────────────────────────────────────────────
function PositionSizer({ settings }) {
  const [form, setForm]     = useState({ entry: '', sl: '', tp1: '', tp2: '', direction: 'BUY' });
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calculate = () => {
    const e = parseFloat(form.entry), s = parseFloat(form.sl), t1 = parseFloat(form.tp1);
    if (isNaN(e) || isNaN(s) || isNaN(t1)) return;
    const dollarRisk = calcDollarRisk(settings.account_balance, settings.risk_per_trade_percent);
    const pos  = calcLotSize(dollarRisk, e, s);
    const rr1  = calcRR(e, s, t1);
    const rr2  = form.tp2 ? calcRR(e, s, parseFloat(form.tp2)) : null;
    setResult({ dollarRisk, pos, rr1, rr2 });
  };

  const copy = () => {
    if (!result) return;
    const txt = `TRADE PLAN — XAU/USD ${form.direction}
Entry  : ${form.entry}
SL     : ${form.sl} (${result.pos?.slDistance} pts / ${result.pos?.slPips} pips)
TP1    : ${form.tp1} — R:R ${result.rr1?.text}
${form.tp2 ? `TP2    : ${form.tp2} — R:R ${result.rr2?.text}` : ''}
Lot Size : ${result.pos?.lots} lots (${result.pos?.microLots} × 0.01)
Risk   : $${result.dollarRisk} (${settings.risk_per_trade_percent}% of $${settings.account_balance})`;
    navigator.clipboard?.writeText(txt);
  };

  return (
    <div style={S.card()}>
      <div style={S.section}>POSITION SIZER — XAU/USD</div>

      {/* Direction */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {['BUY', 'SELL'].map(d => (
          <button key={d} style={{ ...S.btn(form.direction === d ? (d === 'BUY' ? 'green' : 'red') : ''), flex: 1 }}
            onClick={() => set('direction', d)}>
            {d === 'BUY' ? '▲ BUY (LONG)' : '▼ SELL (SHORT)'}
          </button>
        ))}
      </div>

      <div style={S.row}>
        <Inp label="Entry Price" type="number" step="0.01" placeholder="3500.00" value={form.entry} onChange={e => set('entry', e.target.value)} />
        <Inp label="Stop Loss" type="number" step="0.01" placeholder="3480.00" value={form.sl} onChange={e => set('sl', e.target.value)} />
      </div>
      <div style={S.row}>
        <Inp label="TP1 (50%)" type="number" step="0.01" placeholder="3560.00" value={form.tp1} onChange={e => set('tp1', e.target.value)} />
        <Inp label="TP2 (optional)" type="number" step="0.01" placeholder="3620.00" value={form.tp2} onChange={e => set('tp2', e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button style={{ ...S.btn('primary'), flex: 1 }} onClick={calculate}>Calculate</button>
        {result && <button style={S.btn()} onClick={copy}>Copy Plan</button>}
      </div>

      {result && (
        <div style={{ marginTop: '14px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
            <div>
              <div style={S.label}>Lot Size</div>
              <div style={S.stat(THEME.colors.gold)}>{result.pos?.lots} <span style={{ fontSize: '12px' }}>lots</span></div>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{result.pos?.microLots} × 0.01 micro lots</div>
            </div>
            <div>
              <div style={S.label}>Dollar Risk</div>
              <div style={S.stat(THEME.colors.red)}>${result.dollarRisk}</div>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{settings.risk_per_trade_percent}% of account</div>
            </div>
            <div>
              <div style={S.label}>SL Distance</div>
              <div style={S.stat(THEME.colors.text)}>{result.pos?.slDistance} pts</div>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{result.pos?.slPips} pips</div>
            </div>
            <div>
              <div style={S.label}>R:R Ratio</div>
              <div style={S.stat(result.rr1?.ratio >= 2 ? THEME.colors.green : THEME.colors.gold)}>
                {result.rr1?.text}
              </div>
              {result.rr2 && <div style={{ fontSize: '11px', color: THEME.colors.blue }}>TP2: {result.rr2.text}</div>}
            </div>
          </div>

          {result.rr1?.ratio < 2 && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: THEME.colors.gold, padding: '8px', background: 'rgba(255,215,0,0.08)', borderRadius: '6px' }}>
              ⚠️ R:R below 1:2 — consider widening TP or tightening SL before taking this trade
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Risk Dashboard ──────────────────────────────────────────────────────────
function RiskDashboard({ settings }) {
  const trades     = getTrades();
  const today      = calcTodayStats(trades);
  const status     = getRiskStatus(today, settings);
  const maxLoss    = calcDollarRisk(settings.account_balance, settings.max_daily_loss_percent);
  const lossToday  = Math.abs(Math.min(today.pnl, 0));

  return (
    <div style={S.card()}>
      <div style={S.section}>TODAY'S RISK DASHBOARD</div>

      {/* Status badge */}
      <div style={{ ...S.card(`rgba(${status.level === 'GREEN' ? '0,255,136' : status.level === 'YELLOW' ? '255,215,0' : '255,51,51'},0.1)`), padding: '12px 14px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: status.color }}>{status.label}</div>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{status.reason}</div>
        </div>
        <div style={{ fontSize: '28px' }}>
          {status.level === 'GREEN' ? '🟢' : status.level === 'YELLOW' ? '🟡' : '🔴'}
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={S.label}>Trades</div>
          <div style={S.stat(THEME.colors.blue)}>{today.tradeCount}<span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>/{settings.max_daily_trades}</span></div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={S.label}>Today P&L</div>
          <div style={S.stat(today.pnl >= 0 ? THEME.colors.green : THEME.colors.red)}>
            {today.pnl >= 0 ? '+' : ''}${today.pnl}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={S.label}>Win / Loss</div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>
            <span style={{ color: THEME.colors.green }}>{today.wins}W</span>
            <span style={{ color: THEME.colors.textMuted }}> / </span>
            <span style={{ color: THEME.colors.red }}>{today.losses}L</span>
          </div>
        </div>
      </div>

      {/* Daily loss limit bar */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
          <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Daily Loss Limit</span>
          <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>${lossToday.toFixed(2)} / ${maxLoss}</span>
        </div>
        <Bar value={lossToday} max={maxLoss} color={THEME.colors.green} />
      </div>

      {/* Trade count bar */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
          <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Trades Used</span>
          <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{today.tradeCount} / {settings.max_daily_trades}</span>
        </div>
        <Bar value={today.tradeCount} max={settings.max_daily_trades} color={THEME.colors.blue} />
      </div>

      {status.level === 'RED' && (
        <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(255,51,51,0.1)', borderRadius: '8px', fontSize: '12px', color: THEME.colors.red, textAlign: 'center' }}>
          🚫 Sir, daily limit reached. No more trades today. Come back tomorrow fresh.
        </div>
      )}
    </div>
  );
}

// ─── Account Overview ────────────────────────────────────────────────────────
function AccountCard({ settings }) {
  const dollarRisk = calcDollarRisk(settings.account_balance, settings.risk_per_trade_percent);
  const maxLoss    = calcDollarRisk(settings.account_balance, settings.max_daily_loss_percent);

  return (
    <div style={{ ...S.card('rgba(255,215,0,0.08)'), padding: '12px 14px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', textAlign: 'center' }}>
        <div>
          <div style={S.label}>Account</div>
          <div style={S.stat(THEME.colors.gold)}>${settings.account_balance.toLocaleString()}</div>
        </div>
        <div>
          <div style={S.label}>Per Trade ({settings.risk_per_trade_percent}%)</div>
          <div style={S.stat(THEME.colors.green)}>$  {dollarRisk}</div>
        </div>
        <div>
          <div style={S.label}>Max Day Loss ({settings.max_daily_loss_percent}%)</div>
          <div style={S.stat(THEME.colors.red)}>-${maxLoss}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function RiskManager() {
  useJarvisContext();
  const [settings, setSettings] = useState(getRiskSettings());
  const [tab, setTab]           = useState('sizer');

  const handleSave = (newSettings) => {
    updateRiskSettings(newSettings);
    setSettings(newSettings);
  };

  const tabs = [
    { id: 'sizer',     label: '🎯 Position Sizer' },
    { id: 'dashboard', label: '📊 Risk Dashboard' },
    { id: 'settings',  label: '⚙️ Settings' },
  ];

  return (
    <div style={S.page}>
      <div style={S.title}>🛡️ RISK MANAGER</div>
      <div style={S.sub}>POSITION SIZING · DAILY LIMITS · ACCOUNT PROTECTION</div>

      <AccountCard settings={settings} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.btn(tab === t.id ? 'primary' : ''), flex: 1, fontSize: '11px', padding: '7px 6px' }}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'sizer'     && <PositionSizer  settings={settings} />}
      {tab === 'dashboard' && <RiskDashboard  settings={settings} />}
      {tab === 'settings'  && <SettingsPanel  settings={settings} onSave={handleSave} />}

      {/* Quick reference */}
      <div style={{ ...S.card('rgba(255,215,0,0.06)'), padding: '12px 14px' }}>
        <div style={S.section}>XAU/USD POSITION SIZING RULES</div>
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, lineHeight: '1.8' }}>
          <div>📐 1 lot = 100 oz · $1 move = $100 P&L per lot</div>
          <div>🎯 Lot Size = Risk$ ÷ (SL distance × 100)</div>
          <div>⚠️ Never risk more than {settings.risk_per_trade_percent}% per trade = ${calcDollarRisk(settings.account_balance, settings.risk_per_trade_percent)}</div>
          <div>🚫 Stop at ${calcDollarRisk(settings.account_balance, settings.max_daily_loss_percent)} daily loss — {settings.max_daily_trades} trades max</div>
          <div>✅ Minimum R:R = 1:2 before any trade</div>
        </div>
      </div>
    </div>
  );
}


